import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import { CourseContext } from "../CourseContext";
import { getChallengeQuestion, validateChallengeAnswer, markTestFailed } from "../../../services/api";
import "../../../style.css";

const TIMER_DURATION_SECONDS = 45;

const SecurityChallengeModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    showChallengeModal,
    setShowChallengeModal,
    challengeQuestion,
    setChallengeQuestion,
    challengeAnswer,
    setChallengeAnswer,
    challengeSubmitting,
    setChallengeSubmitting,
    totalTimeElapsed,
    askedQuestionIds,
    setAskedQuestionIds,
    startcourse,
  } = useContext(CourseContext);

  const [timerKey, setTimerKey] = useState(0);
  const [timerDuration, setTimerDuration] = useState(TIMER_DURATION_SECONDS);
  const [timeRemaining, setTimeRemaining] = useState(45);
  const [initialRemainingTime, setInitialRemainingTime] = useState(TIMER_DURATION_SECONDS);
  // attemptCount: number of failed submissions for this modal opening (0..2). Third fail => mark failed.
  const [attemptCount, setAttemptCount] = useState(0);
  const [showTimeoutScreen, setShowTimeoutScreen] = useState(false);
  const [failureReason, setFailureReason] = useState("Time limit exceeded");
  // Capture "first attempt vs retake" once when the modal opens.
  // Do NOT recompute it later from sessionStorage because backend calls can mutate user statuses,
  // which would cause the UI message to flip incorrectly.
  const [isFirstTest, setIsFirstTest] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const syncIsFirstTestFromSession = () => {
    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "{}");
      const retakeStatus = String(user?.retake_status ?? "0");
      console.log("[SecurityChallengeModal] retakeStatus:", retakeStatus);
      setIsFirstTest(retakeStatus === "0");
    } catch (_) {
      setIsFirstTest(true);
    }
  };

  // Debug: Log when showChallengeModal changes
  useEffect(() => {
    console.log("[SecurityChallengeModal] showChallengeModal changed to:", showChallengeModal);
  }, [showChallengeModal]);

  // Close modal when navigating away (e.g., to result card or other routes)
  useEffect(() => {
    // If we're navigating to result card or away from course content, close the modal
    if (showChallengeModal && ( location.pathname === '/course/course-result-card')) {
      console.log("[SecurityChallengeModal] Route changed to:", location.pathname, "- closing modal");
      setShowChallengeModal(false);
      setChallengeQuestion(null);
      setChallengeAnswer("");
      setError("");
    }
  }, [location.pathname, showChallengeModal, setShowChallengeModal]);

  // Note: fetchChallengeQuestion is now called in the reset effect above

  // Reset timer and all state when modal opens (for new breakpoint)
  useEffect(() => {
    if (showChallengeModal) {
      // Completely reset all modal state when it opens
      console.log("[SecurityChallengeModal] Modal opened - resetting all state");
      setTimerKey((prev) => prev + 1); // Reset timer key to restart countdown
      setTimerDuration(TIMER_DURATION_SECONDS);
      setInitialRemainingTime(TIMER_DURATION_SECONDS);
      setTimeRemaining(TIMER_DURATION_SECONDS);
      setChallengeAnswer("");
      setError("");
      setChallengeSubmitting(false); // Reset submitting state
      setChallengeQuestion(null); // Clear previous question
      setIsLoading(false);
      setAttemptCount(0);
      setShowTimeoutScreen(false);
      setFailureReason("Time limit exceeded");

      // Determine first-test vs retake for timeout messaging
      syncIsFirstTestFromSession();
    }
  }, [showChallengeModal, setChallengeQuestion, setChallengeAnswer, setChallengeSubmitting]);

  // Fetch challenge question when modal opens and question is cleared
  useEffect(() => {
    if (showChallengeModal && !challengeQuestion && !isLoading) {
      // Use setTimeout to ensure state is reset before fetching
      const timer = setTimeout(() => {
        fetchChallengeQuestion();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showChallengeModal, challengeQuestion, isLoading]);

  const fetchChallengeQuestion = async () => {
    setIsLoading(true);
    setError("");
    try {
      const token = sessionStorage.getItem("token");
      const user_id = sessionStorage.getItem("user_id");
      
      const response = await getChallengeQuestion(token, user_id, totalTimeElapsed);
      
      console.log("[SecurityChallenge] API Response:", response.data);
      console.log("[SecurityChallenge] Response structure - response.data:", response.data, "response.data.data:", response.data.data);
      
      // Check nested structure (response.data.data.question) first, then direct (response.data.question)
      const question = response.data.data?.question ?? response.data.question ?? null;
      
      if (question && question.id) {
        console.log("[SecurityChallenge] ✅ Question loaded:", question);
        
        // Check if this question was already asked
        if (askedQuestionIds.includes(question.id)) {
          // If already asked, fetch again (backend should randomize)
          // For now, we'll allow it but log a warning
          console.warn("[SecurityChallenge] Question already asked, but proceeding:", question.id);
        }
        
        setChallengeQuestion(question);
        // Add question ID to asked list
        setAskedQuestionIds((prev) => {
          const updated = [...prev, question.id];
          sessionStorage.setItem("asked_question_ids", JSON.stringify(updated));
          return updated;
        });
      } else {
        console.error("[SecurityChallenge] ❌ Question not found in response. Full response:", response.data);
        setError("Failed to load challenge question. Please try again.");
      }
    } catch (error) {
      console.error("[SecurityChallenge] Error fetching question:", error);
      setError("Failed to load challenge question. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (showTimeoutScreen) return;
    if (!challengeAnswer.trim()) {
      setError("Please provide an answer");
      return;
    }

    setChallengeSubmitting(true);
    setError("");

    try {
      const token = sessionStorage.getItem("token");
      const user_id = sessionStorage.getItem("user_id");

      const response = await validateChallengeAnswer(token, {
        user_id: user_id,
        question_id: challengeQuestion.id,
        answer: challengeAnswer.trim(),
        time_elapsed: totalTimeElapsed,
      });

      console.log("[SecurityChallenge] Validation Response:", response.data);
      console.log("[SecurityChallenge] Response structure - response.data:", response.data, "response.data.data:", response.data.data);

      // Check nested structure (response.data.data) first, then direct (response.data)
      const responseData = response.data.data ?? response.data;
      const isCorrect = responseData?.correct === true;
      const isFailed = responseData?.failed === true || responseData?.status === "failed";
      const userData = responseData?.user;

      // Update user data in sessionStorage if provided
      if (userData) {
        sessionStorage.setItem("user", JSON.stringify(userData));
        console.log("[SecurityChallenge] ✅ User data updated in sessionStorage:", userData);
      }

      if (isCorrect) {
        // Correct answer - close modal and continue
        console.log("[SecurityChallenge] ✅ Answer is correct!");
        setShowChallengeModal(false);
        setChallengeQuestion(null);
        setChallengeAnswer("");
        setError("");
        setChallengeSubmitting(false);
      } else if (isFailed) {
        // Wrong answer - allow for 2 more tries if they DID put in an answer.
        if (attemptCount < 2) {
          const nextCount = attemptCount + 1;
          console.log("[SecurityChallenge] ❌ Incorrect answer, retry allowed. failedCount:", nextCount);
          setAttemptCount(nextCount);

          // Required message
          setError("Invalid answer, try again.");

          // Clear answer for retry
          setChallengeAnswer("");
          setChallengeSubmitting(false);
          return;
        }

        // Third incorrect attempt -> fail test
        console.log("[SecurityChallenge] ❌ Incorrect answer, max attempts reached -> failing test");
        // Show the same guidance screen as timeout (per requirements), then fail on OK.
        setFailureReason(responseData?.message || "Incorrect answer provided");
        setShowTimeoutScreen(true);
        setChallengeSubmitting(true);
        setError("");
        return;
      } else {
        // Unexpected response structure
        console.error("[SecurityChallenge] ⚠️ Unexpected response structure:", responseData);
        setError("Failed to validate answer. Please try again.");
        setChallengeSubmitting(false);
      }
    } catch (error) {
      console.error("[SecurityChallenge] Error validating answer:", error);
      console.error("[SecurityChallenge] Error response:", error.response?.data);
      
      // Check if backend explicitly marked as failed
      const errorData = error.response?.data?.data ?? error.response?.data;
      
      // Update user data from error response if provided
      if (errorData?.user) {
        sessionStorage.setItem("user", JSON.stringify(errorData.user));
        console.log("[SecurityChallenge] ✅ User data updated from error response:", errorData.user);
      }
      
      if (errorData?.failed === true || errorData?.status === "failed") {
        if (attemptCount < 2) {
          const nextCount = attemptCount + 1;
          console.log("[SecurityChallenge] ❌ Incorrect answer (error response), retry allowed. failedCount:", nextCount);
          setAttemptCount(nextCount);
          setError("Invalid answer, try again.");

          setChallengeAnswer("");
          setChallengeSubmitting(false);
          return;
        }

        // Show the same guidance screen as timeout (per requirements), then fail on OK.
        setFailureReason(errorData?.message || "Incorrect answer");
        setShowTimeoutScreen(true);
        setChallengeSubmitting(true);
        setError("");
        return;
      } else {
        setError("Failed to validate answer. Please try again.");
        setChallengeSubmitting(false);
      }
    }
  };

  const handleTestFailure = async (reason) => {
    try {
      const token = sessionStorage.getItem("token");
      const user_id = sessionStorage.getItem("user_id");

      // Call API to mark test as failed
      const response = await markTestFailed(token, {
        user_id: user_id,
        question_id: challengeQuestion?.id || null,
        answer: challengeAnswer?.trim() || null,
        time_elapsed: totalTimeElapsed,
        reason: reason || "Time limit exceeded"
      });

      console.log("[SecurityChallenge] Mark test failed response:", response.data);

      // Update user data in sessionStorage if provided
      const responseData = response.data?.data ?? response.data;
      if (responseData?.user) {
        sessionStorage.setItem("user", JSON.stringify(responseData.user));
        console.log("[SecurityChallenge] ✅ User data updated in sessionStorage:", responseData.user);
      }

      // Close modal before navigation
      setShowChallengeModal(false);
      setChallengeQuestion(null);
      setChallengeAnswer("");
      setError("");

      // Navigate to course result card
      navigate("/course/course-result-card");
    } catch (error) {
      console.error("[SecurityChallenge] Error marking test as failed:", error);
      
      // Try to update user data from error response if available
      const errorData = error.response?.data?.data ?? error.response?.data;
      if (errorData?.user) {
        sessionStorage.setItem("user", JSON.stringify(errorData.user));
        console.log("[SecurityChallenge] ✅ User data updated from error response:", errorData.user);
      }
      
      // Close modal before navigation even if API call fails
      setShowChallengeModal(false);
      setChallengeQuestion(null);
      setChallengeAnswer("");
      setError("");
      
      // Still navigate to course result card even if API call fails
      navigate("/course/course-result-card");
    }
  };

  const handleTimerComplete = () => {
    console.log("[SecurityChallenge] ⏰ Timer completed - time limit exceeded");
    // Show required messaging + OK button; navigation happens on OK.
    setFailureReason("Time limit exceeded");
    setShowTimeoutScreen(true);
    setChallengeSubmitting(true);
  };

  const handleTimeoutOk = async () => {
    await handleTestFailure(failureReason || "Time limit exceeded");
  };

  const renderTime = ({ remainingTime }) => {
    if (remainingTime === 0) {
      return <div className="timer-text">Time's Up!</div>;
    }

    return (
      <div className="timer-content" style={{textAlign: "center"}}>
        <div className="timer-value">{remainingTime}</div>
        <div className="timer-label">seconds</div>
      </div>
    );
  };

  if (!showChallengeModal) {
    return null;
  }

  return (
    <div
      className="modal fade show"
      style={{
        display: "block",
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        zIndex: 9999,
      }}
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="securityChallengeModalLabel"
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "600px" }}>
        <div className="modal-content" style={{ borderRadius: "10px", border: "none" }}>
          <div className="modal-header" style={{ 
            backgroundColor: "var(--primary-color)", 
            color: "#FFF",
            borderRadius: "10px 10px 0 0",
            borderBottom: "none",
            padding: "20px"
          }}>
            <h4 className="modal-title" id="securityChallengeModalLabel" style={{ margin: 0, fontWeight: 600 }}>
              Security Challenge Question
            </h4>
          </div>
          
          <div className="modal-body" style={{ padding: "30px" }}>
            {showTimeoutScreen ? (
              <div className="text-center py-2">
                <div className="mb-3">
                 
                  <p style={{ lineHeight: 1.5 }}>
                    {isFirstTest ? (
                      <>
                        You did not answer the identity verification question correctly within the required time. As a result, this attempt has ended and will not count toward course completion.
                        <br />
                        <br />
                        You have one free retake available. To try again, please restart the course using your free retake.
                      </>
                    ) : (
                      <>
                        You did not answer the identity verification question correctly within the required time. To continue the course, you will need to purchase a new attempt.
                      </>
                    )}
                  </p>
                </div>
                <div className="d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-primary-s"
                    onClick={handleTimeoutOk}
                    style={{ minWidth: "120px" }}
                  >
                    OK
                  </button>
                </div>
              </div>
            ) : isLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary-s" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading challenge question...</p>
              </div>
            ) : challengeQuestion ? (
              <>
                <div className="d-flex justify-content-center mb-4">
                  <CountdownCircleTimer
                    key={timerKey}
                    isPlaying={showChallengeModal && !challengeSubmitting}
                    duration={timerDuration}
                    initialRemainingTime={initialRemainingTime}
                    colors={["#0BA8CF", "#F7B801", "#A30000"]}
                    colorsTime={[timerDuration, 15, 0]}
                    size={120}
                    strokeWidth={8}
                    onUpdate={(remainingTime) => setTimeRemaining(remainingTime)}
                    onComplete={() => {
                      handleTimerComplete();
                      return { shouldRepeat: false, delay: 0 };
                    }}
                  >
                    {renderTime}
                  </CountdownCircleTimer>
                </div>
                
                <div className="text-center mb-3">
                  <small className="text-muted">
                    Attempts Remaining : {Math.max(0, 3 - attemptCount)}
                  </small>
                </div>

                <div className="challenge-question mb-4">
                  <h5 className="mb-3" style={{ color: "var(--secondary-color)", fontWeight: 600 }}>
                    {challengeQuestion.question}
                  </h5>
                  
                  <div className="form-floating">
                    <input
                      type="text"
                      className={`form-control ${error ? "is-invalid" : ""}`}
                      id="challengeAnswer"
                      placeholder="Enter your answer"
                      value={challengeAnswer}
                      onChange={(e) => {
                        setChallengeAnswer(e.target.value);
                        setError("");
                      }}
                      disabled={challengeSubmitting}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && !challengeSubmitting) {
                          handleSubmit();
                        }
                      }}
                      autoFocus
                    />
                    <label htmlFor="challengeAnswer">Your Answer</label>
                    {error && (
                      <div className="invalid-feedback" style={{ display: "block" }}>
                        {error}
                      </div>
                    )}
                  </div>
                </div>

                <div className="d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-primary-s"
                    onClick={handleSubmit}
                    disabled={challengeSubmitting || !challengeAnswer.trim()}
                    style={{ minWidth: "120px" }}
                  >
                    {challengeSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Submitting...
                      </>
                    ) : (
                      "Submit Answer"
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-danger">Failed to load challenge question.</p>
                <button
                  type="button"
                  className="btn btn-primary-s"
                  onClick={fetchChallengeQuestion}
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityChallengeModal;

